import React, { Component, memo } from 'react';

import cn from 'clsx';
import { EditorState, convertFromRaw, convertToRaw } from 'draft-js';
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';
import { Editor } from 'react-draft-wysiwyg';

import { Box } from '@mui/material';

import { LargeHeaderIcon } from '~/components/Icons/LargeHeaderIcon';
import { SmallHeaderIcon } from '~/components/Icons/SmallHeaderIcon';

type MarkdownEditorProps = {
  value?: string;
  title?: string;
  label?: string;
  showLabel?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  wrapperId?: string;
  placeholder?: string;
  onChange?: (markdown: string) => void;
  onBlur?: (markdown: string) => void;
};

type MarkdownEditorState = {
  editorState: EditorState;
};

const localization = {
  locale: 'en',
  translations: {
    'components.controls.blocktype.h3': (
      <span title="Large Header">
        <LargeHeaderIcon />
      </span>
    ),
    'components.controls.blocktype.h4': (
      <span title="Small Header">
        <SmallHeaderIcon />
      </span>
    ),
  },
};

const toolbar = {
  options: ['inline', 'blockType', 'list', 'link', 'history'],
  inline: {
    options: ['bold'],
  },
  blockType: {
    inDropdown: false,
    options: ['H3', 'H4'],
  },
  list: {
    options: ['ordered', 'unordered'],
  },
  history: {
    options: ['undo', 'redo'],
    title: 'History',
  },
};

const EDITOR_STYLE = {
  padding: '0 12px',
};

type EditorWrapperProps = {
  onBlur: (e: unknown, editorState: EditorState) => void;
  readOnly: boolean;
  wrapperId?: string;
  placeholder?: string;
  onChange: (editorState: EditorState) => void;
  editorState: EditorState;
};

const EditorWrapper = memo(function EditorWrapper(props: EditorWrapperProps) {
  const { onBlur, readOnly, wrapperId, placeholder, onChange, editorState } = props;

  return (
    <Editor
      onEditorStateChange={onChange}
      onBlur={onBlur}
      readOnly={readOnly}
      editorState={editorState}
      toolbar={toolbar}
      toolbarHidden={readOnly}
      wrapperId={wrapperId}
      placeholder={placeholder}
      localization={localization}
      editorStyle={EDITOR_STYLE}
    />
  );
});

class MarkdownEditor extends Component<MarkdownEditorProps, MarkdownEditorState> {
  constructor(props: MarkdownEditorProps) {
    super(props);
    const defaultValue = props.value || '';
    let newEditorState: EditorState;
    if (defaultValue !== '') {
      const contentState = markdownToDraft(defaultValue);
      newEditorState = EditorState.createWithContent(convertFromRaw(contentState));
    } else {
      newEditorState = EditorState.createEmpty();
    }

    this.state = {
      editorState: newEditorState,
    };
  }

  componentDidUpdate(prevProps: MarkdownEditorProps) {
    if (this.props.value !== prevProps.value) {
      const contentState = markdownToDraft(this.props.value);
      const newEditorState = EditorState.createWithContent(convertFromRaw(contentState));
      this.setState({ editorState: newEditorState });
    }
  }

  onEditorStateChange = (editorState: EditorState) => {
    this.setState({ editorState });
    const rawContentState = convertToRaw(editorState.getCurrentContent());
    let markdown = draftToMarkdown(rawContentState);

    if (this.props.onChange) {
      if (markdown.length === 2) {
        markdown = '';
      }
      this.props.onChange(markdown);
    }
  };

  onBlur = (_e: unknown, editorState: EditorState) => {
    if (this.props.onBlur) {
      const rawContentState = convertToRaw(editorState.getCurrentContent());
      const markdown = draftToMarkdown(rawContentState);
      this.props.onBlur(markdown);
    }
  };

  render() {
    return (
      <div>
        {!!this.props.title && <label>{this.props.title}</label>}
        {this.props.showLabel && !!this.props.label && <label>{this.props.label}</label>}
        <Box
          sx={{
            border: '1px solid #ccc',
            backgroundColor: '#fff',
            '&.is-readonly': {
              backgroundColor: '#eeeeee',
            },
          }}
          className={cn({
            'is-readonly': this.props.readonly || this.props.disabled,
          })}
        >
          <EditorWrapper
            onBlur={this.onBlur}
            onChange={this.onEditorStateChange}
            editorState={this.state.editorState}
            readOnly={this.props.readonly || this.props.disabled}
            wrapperId={this.props.wrapperId}
            placeholder={this.props.placeholder}
          />
        </Box>
      </div>
    );
  }
}

export default MarkdownEditor;
